import React from "react";
import { graphql, useFragment } from "react-relay";

import { faExternalLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { SmartStarButton } from "./StarButton";
import { Tag } from "./Tag";
import { SmartTemplateAuthor } from "./TemplateAuthor";

import type { MetadataNoteTemplate$key } from "~/__generated__/MetadataNoteTemplate.graphql";

import styles from "./css/Metadata.module.css";

export function TemplateMetadata({
	className,
	template: templateKey,
}: {
	template: MetadataNoteTemplate$key;
	className?: string;
}) {
	let template;
	const { title, summary, tags, externalUrl } = (template = useFragment(
		graphql`
			fragment MetadataNoteTemplate on NoteTemplateNode {
				title
				summary
				tags
				externalUrl
				author {
					__typename

					... on User {
						isViewer
					}

					...TemplateAuthor_Author
				}
				...StarButtonNoteTemplate
			}
		`,
		templateKey,
	));

	// The only time you can't star a template is when it's owned by another user
	// (i.e. template.user == otherUser). We can detect that by checking if the
	// author of the template is a User and if that user is not the current one.
	const canStar = !(template.author?.__typename === "User" && !template.author.isViewer);

	return (
		<article className={className}>
			<header>
				<div className={styles.authorRow}>
					<SmartTemplateAuthor author={template.author} />
					{canStar && <SmartStarButton template={template} className={styles.star} />}
				</div>
				<h1 className={styles.title}>{title}</h1>
			</header>
			<p className={styles.description}>{summary}</p>
			<section className={styles.tags}>
				{tags.map(tag => (
					<Tag key={tag} name={tag} className={styles.tag} />
				))}
			</section>
			{externalUrl && (
				<footer className={styles.footer}>
					<a href={externalUrl} rel="noopener" target="_blank" className={styles.link}>
						Learn more about this template{" "}
						<FontAwesomeIcon icon={faExternalLink} fixedWidth className={styles.externalIcon} />
					</a>
				</footer>
			)}
		</article>
	);
}
